import { useEffect, useMemo, useState } from 'react';
import { cashMovementsAPI, getApiErrorMessage } from '@matix/api-client';
import type { CashMovementReasonDto, CashMovementType } from '@matix/api-client';
import { useUI } from '../../store/ui';
import { amountStep, amountText } from '../../store/currency';
import { toast } from '../../store/toast';
import { XIcon } from '../../components/icons';
import { useLang } from '../../lib/language';
import { useExpensesAvailable } from '../expenses';

type Tr = (opts: { en: string; ar: string }) => string;

/**
 * Cash movement modal — Phase 10. Records a drop, expense, or pickup
 * against the cashier's currently-open shift. Backend opens the shift
 * implicitly on first activity, so this modal never has to do it itself.
 *
 * Sign convention: amount is always positive on screen. The movement type
 * the cashier picks determines whether it adds to or subtracts from the
 * expected cash drawer total at close.
 */
export function CashMovementModal() {
  const isOpen = useUI((s) => s.openModal === 'cashMovement');
  const closeModal = useUI((s) => s.closeModal);
  const { t: tr } = useLang();
  // Where the branch has the Expenses screen (the legacy expense voucher, an
  // accounting document that comes off expected cash), an expense is recorded
  // THERE — a second, voucher-less 'expense' here would behave differently at
  // close. Every other tenant keeps the three types exactly as before.
  const vouchersOn = useExpensesAvailable();
  const setTab = useUI((s) => s.setTab);
  const types = vouchersOn ? (['drop', 'pickup'] as const) : (['drop', 'expense', 'pickup'] as const);

  const [type, setType] = useState<CashMovementType>('drop');
  const [amountStr, setAmountStr] = useState('');
  const [reason, setReason] = useState<string>('');
  const [referenceNo, setReferenceNo] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reasons, setReasons] = useState<CashMovementReasonDto[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setType('drop');
    setAmountStr('');
    setReason('');
    setReferenceNo('');
    setNotes('');
    setSubmitting(false);
    let cancelled = false;
    cashMovementsAPI
      .listReasons()
      .then((data) => { if (!cancelled) setReasons(data); })
      .catch((err) => toast(getApiErrorMessage(err), 'error'));
    return () => { cancelled = true; };
  }, [isOpen]);

  const filteredReasons = useMemo(
    () => reasons.filter((r) => r.movement_type === type),
    [reasons, type],
  );

  useEffect(() => { setReason(''); }, [type]);

  if (!isOpen) return null;

  const amount = parseFloat(amountStr);
  const amountValid = Number.isFinite(amount) && amount > 0;
  const canSubmit = amountValid && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await cashMovementsAPI.create({
        movement_type: type,
        amount,
        reason: reason || undefined,
        reference_no: referenceNo.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      toast(tr({ en: `${labelFor(tr, type)} recorded`, ar: `تم تسجيل ${labelFor(tr, type)}` }), 'success');
      closeModal();
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay show" onClick={closeModal}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520 }}
      >
        <div className="modal-header">
          <div
            className="modal-header-img"
            style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}
          >
            $
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">{tr({ en: 'Cash drawer movement', ar: 'حركة درج النقد' })}</div>
            <div className="modal-header-name-ar">
              {vouchersOn
                ? tr({ en: 'Drop or pickup', ar: 'إيداع أو سحب' })
                : tr({ en: 'Drop, expense, or pickup', ar: 'إيداع أو مصروف أو سحب' })}
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={closeModal}
            aria-label={tr({ en: 'Close', ar: 'إغلاق' })}
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {types.map((t) => (
              <button
                key={t}
                type="button"
                className={`mod-option ${type === t ? 'selected' : ''}`}
                onClick={() => setType(t)}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                <span className="mod-option-name">{labelFor(tr, t)}</span>
              </button>
            ))}
          </div>
          {vouchersOn && (
            <button
              type="button"
              className="btn-link"
              style={{ alignSelf: 'flex-start', background: 'none', border: 0, padding: 0, color: 'var(--primary)', fontWeight: 700, cursor: 'pointer' }}
              onClick={() => {
                closeModal();
                setTab('expenses');
              }}
            >
              {tr({ en: 'Paying an expense? Use the Expenses screen →', ar: 'لصرف مصروف استخدم شاشة المصروفات ←' })}
            </button>
          )}

          <div>
            <label className="mod-group-title" style={{ display: 'block', marginBottom: 6 }}>
              {tr({ en: 'Amount', ar: 'مبلغ' })}
            </label>
            <input
              type="number"
              inputMode="decimal"
              step={amountStep()}
              min="0"
              className="kitchen-note"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              placeholder={amountText(0)}
              autoFocus
              style={{ minHeight: 0, padding: '10px 12px' }}
            />
          </div>

          <div>
            <label className="mod-group-title" style={{ display: 'block', marginBottom: 6 }}>
              {tr({ en: 'Reason', ar: 'السبب' })}
            </label>
            <select
              className="kitchen-note"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{ minHeight: 0, padding: '10px 12px' }}
            >
              <option value="">{tr({ en: '— Select reason —', ar: '— اختر السبب —' })}</option>
              {filteredReasons.map((r) => (
                <option key={r.reason} value={r.reason}>
                  {r.label_en}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mod-group-title" style={{ display: 'block', marginBottom: 6 }}>
              {tr({ en: 'Reference no. (optional)', ar: 'رقم المرجع (اختياري)' })}
            </label>
            <input
              type="text"
              className="kitchen-note"
              value={referenceNo}
              onChange={(e) => setReferenceNo(e.target.value)}
              placeholder={tr({ en: 'Receipt # / voucher # / etc', ar: 'رقم الإيصال / رقم السند / إلخ' })}
              maxLength={100}
              style={{ minHeight: 0, padding: '10px 12px' }}
            />
          </div>

          <div>
            <label className="mod-group-title" style={{ display: 'block', marginBottom: 6 }}>
              {tr({ en: 'Notes (optional)', ar: 'ملاحظات (اختياري)' })}
            </label>
            <textarea
              className="kitchen-note"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              placeholder={tr({ en: 'Additional context for the manager', ar: 'سياق إضافي للمدير' })}
            />
          </div>
        </div>

        <div className="modal-footer">
          <div />
          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={closeModal}
              disabled={submitting}
            >
              {tr({ en: 'Cancel', ar: 'إلغاء' })}
            </button>
            <button
              type="button"
              className="btn-primary-modal"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting ? tr({ en: 'Recording…', ar: 'جارٍ التسجيل…' }) : tr({ en: `Record ${labelFor(tr, type)}`, ar: `تسجيل ${labelFor(tr, type)}` })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function labelFor(tr: Tr, t: CashMovementType): string {
  return t === 'drop'
    ? tr({ en: 'Drop', ar: 'إيداع' })
    : t === 'expense'
      ? tr({ en: 'Expense', ar: 'مصروف' })
      : tr({ en: 'Pickup', ar: 'سحب' });
}
